import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  TrendingUp,
  DollarSign,
  Target,
  Users as UsersIcon,
  Loader2,
  BarChart3,
  ShieldCheck,
  Ban,
  MailX,
  MessageCircleOff,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { reportsApi, usersApi, type FinanceType } from "@/lib/api"
import { formatCurrency } from "@shared/utils/currency"
import { timeAgo } from "@shared/utils/date"
import { useAuth } from "@/hooks/useAuth"

export default function ReportsPage() {
  const { user } = useAuth()
  const canSeeCompliance =
    user?.role === "admin" || user?.role === "manager" || user?.role === "compliance"
  const [brokerId, setBrokerId] = useState("")
  const [financeType, setFinanceType] = useState<FinanceType | "">("")
  const [complianceDays, setComplianceDays] = useState<number>(30)

  const compliance = useQuery({
    queryKey: ["reports-compliance", complianceDays],
    queryFn: () => reportsApi.compliance(complianceDays),
    enabled: canSeeCompliance,
  })

  const brokers = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
  })

  const summary = useQuery({
    queryKey: ["reports-summary", { brokerId, financeType }],
    queryFn: () =>
      reportsApi.summary({
        brokerId: brokerId || undefined,
        financeType: (financeType || undefined) as FinanceType | undefined,
      }),
  })
  const brokerReport = useQuery({
    queryKey: ["reports-brokers"],
    queryFn: () => reportsApi.brokers(),
  })
  const categories = useQuery({
    queryKey: ["reports-top-categories"],
    queryFn: () => reportsApi.topCategories(),
  })
  const monthly = useQuery({
    queryKey: ["reports-monthly"],
    queryFn: () => reportsApi.monthly(),
  })

  const maxMonthly = Math.max(1, ...(monthly.data?.months.map((m) => m.value) ?? [1]))

  return (
    <AppShell title="Reports" subtitle="Live performance metrics calculated from your deal data">
      <div className="space-y-6">
        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-muted-foreground font-medium">Filter:</span>
            <select
              className="border-input h-9 rounded-md border bg-background px-3 text-sm"
              value={brokerId}
              onChange={(e) => setBrokerId(e.target.value)}
            >
              <option value="">All brokers</option>
              {brokers.data?.users.map((u) => (
                <option key={u.id} value={u.id}>{u.fullName}</option>
              ))}
            </select>
            <select
              className="border-input h-9 rounded-md border bg-background px-3 text-sm"
              value={financeType}
              onChange={(e) => setFinanceType(e.target.value as FinanceType | "")}
            >
              <option value="">All finance types</option>
              <option value="commercial">Commercial</option>
              <option value="consumer">Consumer</option>
            </select>
          </div>
        </Card>

        {/* Summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            label="Settled This Month"
            primary={
              summary.data ? formatCurrency(summary.data.settledThisMonth.value) : "—"
            }
            sub={`${summary.data?.settledThisMonth.count ?? 0} deal${(summary.data?.settledThisMonth.count ?? 0) === 1 ? "" : "s"}`}
            icon={DollarSign}
            accent="emerald"
            loading={summary.isLoading}
          />
          <SummaryCard
            label="Settled Last Month"
            primary={
              summary.data ? formatCurrency(summary.data.settledLastMonth.value) : "—"
            }
            sub={`${summary.data?.settledLastMonth.count ?? 0} deal${(summary.data?.settledLastMonth.count ?? 0) === 1 ? "" : "s"}`}
            icon={DollarSign}
            accent="green"
            loading={summary.isLoading}
          />
          <SummaryCard
            label="Pipeline Value"
            primary={
              summary.data ? formatCurrency(summary.data.pipelineValue.value) : "—"
            }
            sub={`${summary.data?.pipelineValue.count ?? 0} open`}
            icon={TrendingUp}
            accent="blue"
            loading={summary.isLoading}
          />
          <SummaryCard
            label="Conversion Rate"
            primary={summary.data ? `${summary.data.conversionRate}%` : "—"}
            sub="Last 90 days"
            icon={Target}
            accent="purple"
            loading={summary.isLoading}
          />
        </div>

        {/* Monthly trend */}
        <Card className="p-5">
          <div className="mb-4 flex items-center gap-2">
            <BarChart3 className="text-muted-foreground h-4 w-4" />
            <h2 className="text-sm font-semibold text-foreground">
              Settled — Last 12 Months
            </h2>
          </div>
          {monthly.isLoading ? (
            <div className="grid place-items-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : !monthly.data?.months.length ? (
            <p className="text-muted-foreground py-4 text-sm">
              No settled deals in the last 12 months.
            </p>
          ) : (
            <div className="space-y-2">
              {monthly.data.months.map((m) => (
                <div key={m.month} className="flex items-center gap-3 text-sm">
                  <span className="w-20 shrink-0 text-xs text-muted-foreground">{m.month}</span>
                  <div className="bg-rc-grey-100 relative h-6 flex-1 overflow-hidden rounded">
                    <div
                      className="bg-rc-blue-500 flex h-full items-center justify-end px-2 text-[10px] font-medium text-white"
                      style={{ width: `${Math.max(2, (m.value / maxMonthly) * 100)}%` }}
                    >
                      {formatCurrency(m.value)}
                    </div>
                  </div>
                  <span className="w-16 shrink-0 text-right text-xs text-muted-foreground">
                    {m.count} deal{m.count === 1 ? "" : "s"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Two column: brokers + categories */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card className="p-5">
            <div className="mb-4 flex items-center gap-2">
              <UsersIcon className="text-muted-foreground h-4 w-4" />
              <h2 className="text-sm font-semibold text-foreground">Broker Performance</h2>
              <Badge variant="muted" className="text-[10px]">
                Last 3 months
              </Badge>
            </div>
            {brokerReport.isLoading ? (
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            ) : !brokerReport.data?.brokers.length ? (
              <p className="text-muted-foreground text-sm">No broker data yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <tr>
                    <th className="pb-2 font-medium">Broker</th>
                    <th className="pb-2 text-right font-medium">Total</th>
                    <th className="pb-2 text-right font-medium">Settled</th>
                    <th className="pb-2 text-right font-medium">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {brokerReport.data.brokers.map((b, i) => (
                    <tr key={b.brokerId ?? i} className="border-b border-border last:border-0">
                      <td className="py-2 font-medium text-foreground">
                        {b.brokerName ?? "Unassigned"}
                      </td>
                      <td className="py-2 text-right text-muted-foreground">{b.totalDeals}</td>
                      <td className="py-2 text-right text-muted-foreground">{b.settled}</td>
                      <td className="py-2 text-right font-medium">
                        {formatCurrency(b.settledValue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card className="p-5">
            <div className="mb-4 flex items-center gap-2">
              <BarChart3 className="text-muted-foreground h-4 w-4" />
              <h2 className="text-sm font-semibold text-foreground">Top Finance Categories</h2>
            </div>
            {categories.isLoading ? (
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            ) : !categories.data?.categories.length ? (
              <p className="text-muted-foreground text-sm">No categorised deals yet.</p>
            ) : (
              <ul className="space-y-2">
                {categories.data.categories.map((cat) => (
                  <li key={cat.category ?? "none"} className="flex items-center justify-between text-sm">
                    <span className="text-foreground">{cat.category ?? "Uncategorised"}</span>
                    <span className="text-muted-foreground text-xs">
                      {cat.count} · {formatCurrency(cat.value)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* ===== Communication compliance ===== */}
        {canSeeCompliance && (
          <Card className="p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="text-muted-foreground h-4 w-4" />
                <h2 className="text-sm font-semibold text-foreground">
                  Communication Compliance
                </h2>
              </div>
              <select
                className="border-input h-8 rounded-md border bg-background px-2 text-xs"
                value={complianceDays}
                onChange={(e) => setComplianceDays(Number(e.target.value))}
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last 12 months</option>
              </select>
            </div>

            {compliance.isLoading ? (
              <div className="grid place-items-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !compliance.data ? (
              <p className="text-muted-foreground text-sm">Couldn't load compliance data.</p>
            ) : (
              <ComplianceContent report={compliance.data} />
            )}
          </Card>
        )}
      </div>
    </AppShell>
  )
}

// ============================================================
// Compliance section
// ============================================================
type ComplianceReport = NonNullable<
  ReturnType<typeof reportsApi.compliance> extends Promise<infer T> ? T : never
>

function ComplianceContent({ report }: { report: ComplianceReport }) {
  // Fold totals into a channel+outcome matrix.
  type Key = `${"sms" | "email"}:${"direct" | "marketing"}`
  const grid: Record<Key, { sent: number; skipped: number; failed: number }> = {
    "sms:direct": { sent: 0, skipped: 0, failed: 0 },
    "sms:marketing": { sent: 0, skipped: 0, failed: 0 },
    "email:direct": { sent: 0, skipped: 0, failed: 0 },
    "email:marketing": { sent: 0, skipped: 0, failed: 0 },
  }
  for (const row of report.totals) {
    const key = `${row.channel}:${row.classification}` as Key
    grid[key][row.outcome] = row.count
  }

  const snap = report.contactSnapshot

  return (
    <div className="space-y-6">
      {/* Contact-level snapshot */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MiniStat
          icon={<MailX className="h-4 w-4" />}
          label="Email unsubs"
          value={snap.emailUnsubscribed}
          total={snap.total}
          accent="rose"
        />
        <MiniStat
          icon={<MessageCircleOff className="h-4 w-4" />}
          label="SMS unsubs"
          value={snap.smsUnsubscribed}
          total={snap.total}
          accent="rose"
        />
        <MiniStat
          icon={<Ban className="h-4 w-4" />}
          label="Do not contact"
          value={snap.doNotContact}
          total={snap.total}
          accent="rose"
        />
        <MiniStat
          icon={<ShieldCheck className="h-4 w-4" />}
          label="Contacts (total)"
          value={snap.total}
          total={null}
          accent="blue"
        />
      </div>

      {/* Send-log matrix */}
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Automation sends
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="pb-2 font-medium">Channel / classification</th>
                <th className="pb-2 text-right font-medium">Sent</th>
                <th className="pb-2 text-right font-medium">Skipped</th>
                <th className="pb-2 text-right font-medium">Failed</th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ["sms:marketing", "SMS · Marketing"],
                  ["sms:direct", "SMS · Direct"],
                  ["email:marketing", "Email · Marketing"],
                  ["email:direct", "Email · Direct"],
                ] as Array<[Key, string]>
              ).map(([key, label]) => {
                const cell = grid[key]
                return (
                  <tr key={key} className="border-b border-border last:border-0">
                    <td className="py-2 font-medium text-foreground">{label}</td>
                    <td className="py-2 text-right text-emerald-700">
                      {cell.sent.toLocaleString()}
                    </td>
                    <td className="py-2 text-right text-amber-700">
                      {cell.skipped.toLocaleString()}
                    </td>
                    <td className="py-2 text-right text-rose-700">
                      {cell.failed.toLocaleString()}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top skip codes */}
      {report.skipCodes.length > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Top skip reasons
          </h3>
          <ul className="space-y-1 text-sm">
            {report.skipCodes.slice(0, 10).map((s, i) => (
              <li
                key={`${s.channel}-${s.classification}-${s.skipCode}-${i}`}
                className="flex items-center justify-between rounded border border-border bg-rc-grey-50/50 px-3 py-1.5"
              >
                <span className="text-foreground">
                  <span className="text-muted-foreground">
                    {s.channel} · {s.classification} —{" "}
                  </span>
                  {s.skipCode?.replace(/_/g, " ") ?? "unknown"}
                </span>
                <span className="text-xs text-muted-foreground">{s.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent opt-outs */}
      {report.recentOptOuts.length > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Recent opt-outs
          </h3>
          <ul className="space-y-2">
            {report.recentOptOuts.slice(0, 15).map((r) => (
              <li
                key={r.id}
                className="rounded border border-border bg-rc-grey-50/50 p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 text-sm">
                    <a
                      href={`/contacts/${r.contactId}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      View contact
                    </a>
                    <span className="ml-2 text-muted-foreground">
                      {r.fieldName.replace(/_/g, " ")} → {r.newValue ?? "—"}
                    </span>
                    {r.inboundMessageText && (
                      <p className="mt-1 text-xs italic text-muted-foreground">
                        “{r.inboundMessageText}”
                      </p>
                    )}
                  </div>
                  <span className="flex-none text-xs text-muted-foreground">
                    {timeAgo(r.changedAt)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function MiniStat({
  icon,
  label,
  value,
  total,
  accent,
}: {
  icon: React.ReactNode
  label: string
  value: number
  total: number | null
  accent: "rose" | "blue"
}) {
  const tone =
    accent === "rose"
      ? "bg-rose-50 text-rose-600"
      : "bg-blue-50 text-blue-600"
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <div className={`grid h-7 w-7 place-items-center rounded-md ${tone}`}>{icon}</div>
      </div>
      <div className="mt-1 text-lg font-semibold text-foreground">
        {value.toLocaleString()}
      </div>
      {total != null && total > 0 && (
        <div className="text-[10px] text-muted-foreground">
          {((value / total) * 100).toFixed(1)}% of {total.toLocaleString()}
        </div>
      )}
    </div>
  )
}

function SummaryCard({
  label,
  primary,
  sub,
  icon: Icon,
  accent,
  loading,
}: {
  label: string
  primary: string
  sub: string
  icon: typeof DollarSign
  accent: "emerald" | "green" | "blue" | "purple"
  loading?: boolean
}) {
  const bg = {
    emerald: "bg-emerald-50 text-emerald-600",
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
  }[accent]
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
          {label}
        </span>
        <div className={`grid h-8 w-8 place-items-center rounded-lg ${bg}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-2 text-xl font-semibold text-foreground">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : primary}
      </div>
      <div className="text-muted-foreground text-xs">{sub}</div>
    </Card>
  )
}
